import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Mail, MessageSquare, Send, Sparkles, Wand2, Loader2, MessagesSquare } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import api, { apiError } from "../lib/api";
import { createItem } from "../hooks/useEntity";
import { formatDateTime } from "../lib/utils-format";
import { EngagementHistory } from "../components/LeadEngagement";

const CH_ICON = { Email: Mail, SMS: MessageSquare, "Website Chat": MessagesSquare };

const MSG_STATUS_STYLE = {
  Delivered: "bg-emerald-500/15 text-emerald-400",
  Sent: "bg-amber-500/15 text-amber-400",
  Sending: "bg-amber-500/15 text-amber-400",
  Queued: "bg-amber-500/15 text-amber-400",
  Failed: "bg-red-500/15 text-red-400",
  Undelivered: "bg-red-500/15 text-red-400",
};
const PENDING_STATUSES = ["Sent", "Sending", "Queued", "queued", "sending"];

export default function LeadConversationPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialChannel = searchParams.get("channel") === "sms" ? "SMS" : searchParams.get("channel") === "email" ? "Email" : null;
  const opportunityId = searchParams.get("opp") || null;
  const [lead, setLead] = useState(null);
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [channel, setChannel] = useState(initialChannel || "Email");
  const [filter, setFilter] = useState(initialChannel || "all");
  const [sending, setSending] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [polishing, setPolishing] = useState(false);
  const [histRefresh, setHistRefresh] = useState(0);

  const loadMessages = useCallback((convId) => {
    return api.get(`/messages?conversation_id=${convId}&sort_by=created_at&sort_dir=asc&limit=200`)
      .then((r) => setMessages(r.data.items || []));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get(`/leads/${id}`).then((r) => r.data).catch(() => null),
      api.get(`/conversations?lead_id=${id}&sort_by=last_message_at&sort_dir=desc&limit=1`).then((r) => (r.data.items || [])[0]).catch(() => null),
    ]).then(async ([l, c]) => {
      setLead(l);
      setConv(c || null);
      if (c) {
        await loadMessages(c.id);
        if (!initialChannel) setChannel(c.last_channel || c.channel || "Email");
      }
    }).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [id, loadMessages, initialChannel]);

  useEffect(() => { load(); }, [load]);

  // Poll for Twilio delivery-status callbacks while a message is still in-flight.
  useEffect(() => {
    if (!conv) return;
    const pending = messages.some((m) => m.direction === "outbound" && PENDING_STATUSES.includes(m.status));
    if (!pending) return;
    const t = setInterval(() => loadMessages(conv.id), 8000);
    return () => clearInterval(t);
  }, [messages, conv, loadMessages]);

  const leadName = lead?.company_name || `${lead?.first_name || ""} ${lead?.last_name || ""}`.trim() || "Lead";
  const dncBlocked = channel === "SMS"
    ? (lead?.do_not_text || lead?.do_not_contact)
    : (lead?.do_not_email || lead?.do_not_contact);
  const dncLabel = lead?.do_not_contact ? "Do Not Contact" : channel === "SMS" ? "Do Not Text" : "Do Not Email";
  const channelsUsed = [...new Set(messages.map((m) => m.channel).filter(Boolean))];
  const visibleMessages = filter === "all" ? messages : messages.filter((m) => m.channel === filter);

  const draft = async () => {
    if (!conv) return;
    const last = [...messages].reverse().find((m) => m.direction === "inbound");
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/draft-reply", { conversation_id: conv.id, message: last?.body || conv.subject });
      setReply(data.reply);
    } catch (e) { toast.error(apiError(e)); } finally { setAiLoading(false); }
  };

  const askAngela = async () => {
    setPolishing(true);
    try {
      const ctx = messages.slice(-6).map((m) => `${m.direction === "inbound" ? "Customer" : "Rep"} (${m.channel}): ${m.body}`).join("\n");
      const { data } = await api.post("/ai/polish-message", {
        conversation_id: conv?.id, draft: reply, channel,
        mode: reply.trim() ? "polish" : "suggest", context: ctx,
      });
      setReply(data.message);
      toast.success(reply.trim() ? "Angela polished your message" : "Angela suggested a message");
    } catch (e) { toast.error(apiError(e)); } finally { setPolishing(false); }
  };

  const send = async () => {
    if (!reply.trim()) return;
    if (dncBlocked) { toast.error(`This lead is marked ${dncLabel} — sending is blocked.`); return; }
    const isSms = channel === "SMS";
    const to = isSms ? (lead?.mobile_phone || lead?.business_phone) : lead?.email;
    if (!to) {
      toast.error(`This lead has no ${isSms ? "mobile number" : "email address"} to send to`);
      return;
    }
    setSending(true);
    try {
      let c = conv;
      if (!c) {
        c = await createItem("conversations", {
          lead_id: id, lead_name: leadName, account_id: lead?.account_id, account_name: lead?.company_name,
          channel, subject: `Conversation with ${leadName}`, conversation_status: "Open",
        });
        setConv(c);
      }
      if (isSms) {
        await api.post("/telephony/sms", {
          to, body: reply, lead_id: id, lead_name: leadName,
          product_name: lead?.product_name, conversation_id: c.id, opportunity_id: opportunityId,
        });
      } else {
        await api.post("/email/send", {
          to, subject: c.subject || `Conversation with ${leadName}`, body: reply,
          lead_id: id, lead_name: leadName, product_name: lead?.product_name,
          conversation_id: c.id, opportunity_id: opportunityId,
        });
      }
      setReply("");
      await loadMessages(c.id);
      setHistRefresh((x) => x + 1);
      toast.success(`${channel} sent to ${to}`);
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[900px] mx-auto">
      <button onClick={() => navigate(`/record/analysis/${id}`)} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-5" data-testid="conversation-back-btn">
        <ArrowLeft className="h-4 w-4" />Back to lead
      </button>

      <div className="mb-6 pb-6 border-b border-border">
        <div className="text-[11px] uppercase tracking-[0.18em] text-primary mb-1.5">Conversation</div>
        <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-3" data-testid="conversation-title">
          <MessagesSquare className="h-6 w-6 text-primary" />{leadName}
        </h1>
        <p className="text-sm text-muted-foreground mt-1.5">
          Unified SMS & Email conversation{channelsUsed.length ? ` · ${channelsUsed.join(" · ")}` : ""}
        </p>
      </div>

      {loading ? (
        <div className="space-y-3"><Skeleton className="h-16" /><Skeleton className="h-16" /></div>
      ) : (
        <div className="rounded-lg border border-border bg-card flex flex-col" style={{ minHeight: "60vh" }} data-testid="conversation-panel">
          {/* Separate Email / SMS views */}
          <div className="flex items-center gap-1 px-4 pt-3 border-b border-border" data-testid="conv-view-tabs">
            {[["all", "All", null], ["Email", "Email", Mail], ["SMS", "SMS", MessageSquare]].map(([val, lbl, Icon]) => {
              const n = val === "all" ? messages.length : messages.filter((m) => m.channel === val).length;
              return (
                <button key={val} onClick={() => setFilter(val)} data-testid={`conv-view-${val.toLowerCase()}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${filter === val ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                  {Icon && <Icon className="h-3.5 w-3.5" />}{lbl}
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary">{n}</span>
                </button>
              );
            })}
          </div>
          <div className="flex-1 overflow-y-auto slim-scroll p-5 space-y-4" data-testid="conversation-thread">
            {visibleMessages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground py-16" data-testid="conversation-empty">
                <MessagesSquare className="h-8 w-8 mb-3 opacity-50" />
                <div className="text-sm">{filter === "all" ? "No messages yet. Start the conversation below." : `No ${filter} messages yet.`}</div>
              </div>
            ) : (
              visibleMessages.map((m) => {
                const MIcon = CH_ICON[m.channel] || Mail;
                return (
                  <div key={m.id} className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-lg px-4 py-2.5 text-sm ${m.direction === "outbound" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide opacity-70 mb-1">
                        <MIcon className="h-3 w-3" />
                        <span data-testid={`conv-msg-channel-${m.id}`}>{m.channel || "Email"}</span>
                        <span className="opacity-60">· {m.sender}</span>
                      </div>
                      {m.body}
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className="text-[10px] opacity-60">{formatDateTime(m.created_at)}</span>
                        {m.direction === "outbound" && m.status && (
                          <span
                            data-testid={`conv-msg-status-${m.id}`}
                            className={`text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded-full font-medium ${MSG_STATUS_STYLE[m.status] || "bg-secondary text-muted-foreground"}`}
                            title={m.provider_error || m.status}
                          >
                            {m.status}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="border-t border-border p-4">
            {dncBlocked && (
              <div className="mb-3 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-400" data-testid="conv-dnc-banner">
                This lead is marked <span className="font-semibold">{dncLabel}</span>. Sending {channel === "SMS" ? "texts" : "emails"} to them is blocked.
              </div>
            )}
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Send via:</span>
              {["Email", "SMS"].map((ch) => {
                const Icon = CH_ICON[ch];
                return (
                  <button key={ch} onClick={() => setChannel(ch)} data-testid={`conv-channel-${ch.toLowerCase()}`}
                    className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border transition-colors ${channel === ch ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-accent"}`}>
                    <Icon className="h-3 w-3" />{ch}
                  </button>
                );
              })}
            </div>
            <Textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder={`Write a ${channel} message…`} rows={3} data-testid="conv-reply-input" />
            <div className="flex items-center justify-between mt-2 gap-2">
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={draft} disabled={aiLoading || polishing || !conv} data-testid="conv-ai-draft-btn">
                  {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1.5" />Angela Draft</>}
                </Button>
                <Button size="sm" variant="outline" onClick={askAngela} disabled={polishing || aiLoading} data-testid="conv-angela-polish-btn">
                  {polishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Wand2 className="h-4 w-4 mr-1.5" />{reply.trim() ? "Polish with Angela" : "Suggest with Angela"}</>}
                </Button>
              </div>
              <Button size="sm" onClick={send} disabled={sending || !reply.trim() || dncBlocked} data-testid="conv-send-btn">
                {sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}Send {channel}
              </Button>
            </div>
          </div>
        </div>
      )}

      {!loading && (
        <div className="mt-6">
          <EngagementHistory leadId={id} refreshToken={histRefresh} />
        </div>
      )}
    </div>
  );
}
